/**
 * The platform admin's small pure pieces: when to renew the session, how an
 * address is suggested from a name, how a date-only value is shown.
 *
 * Pure, so they are tested without a browser (`words.test.ts`), in a zone far
 * from UTC so a date that slips a day is caught.
 */

/** Renew once the pass has this long left. */
export const RENEW_WITHIN_MS = 5 * 60_000;
/** Somebody who has not touched the app for this long is not kept signed in. */
export const IDLE_AFTER_MS = 30 * 60_000;

/**
 * Whether to renew the operator's pass now.
 *
 * Only near its end, only while it is still good (an expired one cannot be
 * renewed, and the next request will say so), and only for somebody who has
 * used the app lately: a tab left open overnight on a shared desk should run
 * out, not be kept alive by a timer.
 */
export function shouldRenew({
  expiresAt,
  lastActivityAt,
  now,
}: {
  expiresAt: number;
  lastActivityAt: number;
  now: number;
}): boolean {
  if (expiresAt <= now) return false;
  if (expiresAt - now > RENEW_WITHIN_MS) return false;
  return now - lastActivityAt <= IDLE_AFTER_MS;
}

/** The rule the server holds a restaurant's address to (`NewRestaurant.slug`). */
const ADDRESS = /^[a-z0-9](?:[a-z0-9-]*[a-z0-9])?$/;

export function isAddress(value: string): boolean {
  return value.length >= 3 && value.length <= 63 && ADDRESS.test(value);
}

/**
 * An address suggested from a restaurant's name: "Café Olé, Bogotá" →
 * "cafe-ole-bogota". Letters with accents lose them rather than the whole
 * letter; anything that is not a letter or digit becomes one hyphen. A name
 * in a script with no Latin letters suggests nothing, and the address is
 * typed instead.
 */
export function suggestAddress(name: string): string {
  return name
    .normalize('NFKD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 63)
    .replace(/-+$/g, '');
}

/**
 * Whether an amount typed for an invoice reads as money: digits, and at most
 * two after a point. Checked as text; the server does the arithmetic, and
 * refuses anything this lets through that it does not like.
 */
export function isAmount(value: string): boolean {
  return /^\d{1,10}(\.\d{1,2})?$/.test(value.trim()) && /[1-9]/.test(value);
}

/**
 * A date with no time — a due date, a trading day — in the reader's words.
 *
 * `new Date("2026-10-01")` is midnight UTC, which west of Greenwich is the
 * evening before; formatted in UTC it stays the day it says.
 */
export function formatDay(isoDate: string, locale: string): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return isoDate;
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeZone: 'UTC' }).format(
    new Date(Date.UTC(year, month - 1, day)),
  );
}

/** A moment, on the operator's own clock. */
export function formatMoment(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { dateStyle: 'medium', timeStyle: 'short' }).format(
    new Date(iso),
  );
}

export function cycleWords(cycle: 'month' | 'year' | null | undefined): string {
  if (cycle === 'year') return 'yearly';
  if (cycle === 'month') return 'monthly';
  return '—';
}

/** Base32 in fours: twenty-six unbroken characters is twenty-six chances to slip. */
export function inFours(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}

/**
 * Open past its due date, on the UTC calendar — the same rule the server
 * uses for the invoice list, so a restaurant's page and Billing agree.
 */
export function isOverdue(status: string, dueOn: string, now: Date = new Date()): boolean {
  return status === 'open' && dueOn < now.toISOString().slice(0, 10);
}

/**
 * Features one plan has and another lacks, as keys, in the first plan's
 * order. What a move down (or the end of a free trial) takes away — said on
 * the sheet before it happens.
 */
export function featuresLost(from: readonly string[], to: readonly string[]): string[] {
  const kept = new Set(to);
  return from.filter((key) => !kept.has(key));
}

/** The calendar day (`YYYY-MM-DD`) of a moment, on the UTC calendar the server bills on. */
export function dayOf(iso: string): string {
  return new Date(iso).toISOString().slice(0, 10);
}

/** A calendar day `days` after another, both `YYYY-MM-DD`. */
export function addDays(isoDate: string, days: number): string {
  const [year, month, day] = isoDate.split('-').map(Number);
  if (!year || !month || !day) return isoDate;
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

/** "3 days left", "1 day left", "Ends today". */
export function daysLeftWords(days: number): string {
  if (days <= 0) return 'Ends today';
  return `${days} day${days === 1 ? '' : 's'} left`;
}

/** The operator's own calendar day of a moment, as `yyyy-mm-dd`. */
export function localDay(iso: string): string {
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** A clock time on the operator's own clock. */
export function formatClock(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { timeStyle: 'short' }).format(new Date(iso));
}

/** A day heading: "Today", "Yesterday", or the date with its weekday. */
export function dayHeading(day: string, today: string, locale: string): string {
  if (day === today) return 'Today';
  const [y, m, d] = today.split('-').map(Number);
  const yesterday = localDay(new Date(y!, m! - 1, d! - 1, 12).toISOString());
  if (day === yesterday) return 'Yesterday';
  const [year, month, date] = day.split('-').map(Number);
  return new Intl.DateTimeFormat(locale, {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    ...(year === y ? {} : { year: 'numeric' }),
  }).format(new Date(year!, month! - 1, date!, 12));
}

/** Entries in their order, gathered under the operator's calendar days. */
export function byDay<T extends { occurred_at: string }>(
  entries: readonly T[],
): [string, T[]][] {
  const days: [string, T[]][] = [];
  for (const entry of entries) {
    const day = localDay(entry.occurred_at);
    const last = days[days.length - 1];
    if (last && last[0] === day) last[1].push(entry);
    else days.push([day, [entry]]);
  }
  return days;
}
